import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

type UseCertificateArchiveMutationsOptions = {
  onSuccess?: (data?: unknown) => void
  onError?: (error?: unknown) => void
}

export const useCertificateArchiveMutations = (
  options?: UseCertificateArchiveMutationsOptions
) => {
  const queryClient = useQueryClient()

  const invalidate = (id: string) => {
    queryClient.invalidateQueries({ queryKey: ['certificates', 'detail', id] })
  }

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await createApiCall({
        // PATCH, not POST. The route is @Patch(':certificateId/archive')
        // (certificates.admin.controller.ts:243). POST 404'd on every call since
        // VERA-413 shipped this UI, and it went unnoticed because createApiCall
        // resolved the 404 into a success toast — see the errorHandler fix.
        method: 'PATCH',
        url: `/certificates/admin/${id}/archive`,
      })
      return response.data
    },
    onSuccess: (data, id) => {
      invalidate(id)
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const unarchiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await createApiCall({
        // PATCH — @Patch(':certificateId/unarchive'), same as archive above.
        method: 'PATCH',
        url: `/certificates/admin/${id}/unarchive`,
      })
      return response.data
    },
    onSuccess: (data, id) => {
      invalidate(id)
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    archiveCertificate: (id: string) => archiveMutation.mutate(id),
    unarchiveCertificate: (id: string) => unarchiveMutation.mutate(id),
    isArchiving: archiveMutation.isPending,
    isUnarchiving: unarchiveMutation.isPending,
  }
}
